import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clear, Info, Search } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import {
    Dialog,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Theme,
    withStyles,
} from '@material-ui/core';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import Spin from 'components/atoms/Spin/Spin';
import { DictionaryDto } from 'api/dictionary';
import FlightDetails from 'components/organisms/FlightDetails/FlightDetails';
import CustomDatePicker from 'components/atoms/CustomDatePicker/CustomDatePicker';
import { fetchFlights, fetchFlightStatuses, FlightDto, initFlight } from '../../api/flight';
import { fetchRegionsDict } from '../../api/regions';
import { useSelector } from 'react-redux';
import { getUserInfo } from '../../store/slices/userInfoSlice';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        overflowY: 'visible',
    },
    formControl: {
        minWidth: 120,
        width: '100%',
    },
    buttons: {
        '& button': {
            marginLeft: theme.spacing(2),
            '&:first-child': {
                marginLeft: 0,
            },
        },
    },
    tableRow: {
        cursor: 'pointer',
    },
}));

const getColorByStatus = (status: string | undefined) => {
    switch (status) {
        case 'SEND':
            return '#fff3cd';
        case 'PROCESS':
            return '#d4edda';
        case 'REMOVE':
            return '#f8d7da';
        default:
            return '#fff';
    }
};

const StyledCell = withStyles(() => ({
    root: {
        borderBottom: '1px solid #b3b3b3',
    },
}))(TableCell);

const Flights = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { roles } = useSelector(getUserInfo);
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<FlightDto[]>([]);
    const [item, setItem] = useState<FlightDto>(initFlight());
    const [regionId, setRegionId] = useState<number | string>('');
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [untilDate, setUntilDate] = useState<Date | null>(null);
    const [code, setCode] = React.useState<string | null>(null);
    const [courierName, setCourierName] = React.useState<string | null>(null);
    const [state, setState] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<DictionaryDto[]>([]);
    const [reset, setReset] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);

    const hasAccessToCloseList = useMemo(
        () =>
            roles?.length > 0 && roles?.filter((r) => r === 'ADMIN' || r === 'SUPPORT').length > 0,
        [roles]
    );

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await fetchFlights({
                page,
                size: perPage,
                code: code || null,
                status: state || null,
                courier_fio: courierName,
                date_from: startDate,
                date_until: untilDate,
                region_id: regionId,
            });
            setItems(x.content);
            setTotal(x.total_elements);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [page, perPage, code, state, courierName, startDate, untilDate, regionId, enqueueSnackbar]);

    const getDictionaries = useCallback(async () => {
        try {
            const x = await fetchFlightStatuses();
            setStatuses(x);
            const regionList = await fetchRegionsDict();
            if (regionList?.length === 1) {
                const [currentRegion] = regionList;
                setRegionId(currentRegion.id);
            }
            setRegions(regionList);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении статусов: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        }
    }, [enqueueSnackbar]);

    const [open, setOpen] = useState(false);

    const handleSearch = () => {
        fetch();
    };

    const handleCodeChange = (e: any) => {
        setCode(e.target.value);
    };

    const handleCourierNameChange = (e: any) => {
        setCourierName(e.target.value);
    };

    const handleStateChange = (e: any) => {
        setState(e.target.value);
    };

    const handleEdit = (item: FlightDto) => {
        setItem(item);
        setOpen(true);
    };

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
    };

    const handleUntilDateChange = (date: Date | null) => {
        setUntilDate(date);
    };

    const handleRegionChange = (e: any) => {
        const value = e.target.value as string;
        setRegionId(value);
    };

    const handleReset = () => {
        setRegionId('');
        setState(null);
        setCode('');
        setStartDate(null);
        setUntilDate(null);
        setCourierName('');
        setReset(!reset);
    };

    const handleEnterSearch = (e: any) => {
        if (e.key === 'Enter') {
            fetch();
        }
    };

    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, reset]);

    useEffect(() => {
        getDictionaries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box position={'relative'}>
            <Grid container spacing={3}>
                <Grid item xs={3}>
                    <FormControl
                        className={classes.formControl}
                        variant="outlined"
                        disabled={regions?.length === 1}
                    >
                        <InputLabel id="product-id-label">Регион</InputLabel>
                        <Select
                            labelId="product-id-label"
                            id="product-id"
                            value={regionId}
                            label="Регион"
                            onChange={handleRegionChange}
                        >
                            {regions.length > 1 && (
                                <MenuItem value="">
                                    <em>Не выбрано</em>
                                </MenuItem>
                            )}

                            {regions.map((region, index) => (
                                <MenuItem key={index} value={region.id}>
                                    {region.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl className={classes.formControl} variant="outlined">
                        <InputLabel id="product-id-label">Статус</InputLabel>
                        <Select
                            labelId="product-id-label"
                            id="product-id"
                            value={state}
                            label="Статус"
                            onChange={handleStateChange}
                        >
                            <MenuItem value="">
                                <em>Не выбрано</em>
                            </MenuItem>
                            {statuses.map((statItem, index) => (
                                <MenuItem key={index} value={statItem.code}>
                                    {statItem.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Введите код"
                        fullWidth
                        value={code}
                        variant="outlined"
                        className={classes.formControl}
                        onChange={handleCodeChange}
                        onKeyDown={handleEnterSearch}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={3}>
                    <CustomDatePicker
                        label="Дата с:"
                        value={startDate}
                        onChange={handleStartDateChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <CustomDatePicker
                        label="Дата по:"
                        value={untilDate}
                        onChange={handleUntilDateChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        label="ФИО курьера"
                        fullWidth
                        value={courierName}
                        variant="outlined"
                        className={classes.formControl}
                        onKeyDown={handleEnterSearch}
                        onChange={handleCourierNameChange}
                    />
                </Grid>
                <Grid item xs={3} className={classes.buttons}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                    >
                        Поиск
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReset}
                        startIcon={<Clear />}
                    >
                        Сброс
                    </Button>
                </Grid>
            </Grid>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'small'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Код</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Курьер</TableCell>
                                <TableCell>Внештатный</TableCell>
                                <TableCell>Тип</TableCell>
                                <TableCell>Дата создания</TableCell>
                                <TableCell>Дата завершения</TableCell>
                                <TableCell>Автор</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow
                                    className={classes.tableRow}
                                    key={index}
                                    hover
                                    onDoubleClick={() => handleEdit(item)}
                                    style={{
                                        background: getColorByStatus(
                                            item?.delivery_list_status?.code
                                        ),
                                    }}
                                >
                                    <StyledCell>
                                        <IconButton onClick={() => handleEdit(item)}>
                                            <Info />
                                        </IconButton>
                                    </StyledCell>
                                    <StyledCell>{item.code}</StyledCell>
                                    <StyledCell>
                                        {item.delivery_list_status?.description}
                                    </StyledCell>
                                    <StyledCell>
                                        {[
                                            item.courier?.last_name,
                                            item.courier?.first_name,
                                            item.courier?.phone_number,
                                        ].join(' ')}
                                    </StyledCell>
                                    <StyledCell>
                                        {item.courier?.is_freelancer ? 'Да' : 'Нет'}
                                    </StyledCell>
                                    <StyledCell>
                                        {item.with_payment ? 'С денежной наличностью' : 'Обычный'}
                                    </StyledCell>
                                    <StyledCell>{item.date_created}</StyledCell>
                                    <StyledCell>{item.date_ended}</StyledCell>
                                    <StyledCell>{`${item.creator.last_name} ${item.creator.first_name}`}</StyledCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
            <Dialog open={open} fullWidth={true} maxWidth="lg" onClose={() => setOpen(false)}>
                <DialogTitle>Заявки в рейсе</DialogTitle>
                <DialogContent className={classes.root}>
                    <Box display="flex" flexDirection="column">
                        <FlightDetails
                            id={item.id}
                            item={item}
                            hasAccessToCloseList={hasAccessToCloseList}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Отмена</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Flights;
